import * as React from 'react';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Pagination from '@amzn/awsui-components-console/pagination';
import Toggle from '@amzn/awsui-components-console/toggle';
import Badge from '@amzn/awsui-components-console/badge';
import type { SelectProps } from '@amzn/awsui-components-console/select';
import type { Collection } from '@amzn/aws-gcr-content-connection-management-client';
import TableFilters from '../../../DataSourcesPage/components/TableFilters';
import { getCollectionColumnDefinitions, filterCollections, sortCollections } from '../../../../shared/config/collectionsTableConfig';

// Create column definitions with status indicators
const getCollectionColumnDefinitionsWithStatus = (selectedCollectionUrls: string[], subscribedCollectionUrls: string[]) => {
  const baseColumns = getCollectionColumnDefinitions();

  // Add status column at the beginning
  const statusColumn = {
    id: 'status',
    header: 'Status',
    cell: (collection: Collection) => {
      const isCurrentlySelected = collection.collectionId && selectedCollectionUrls.includes(collection.collectionId);
      const wasOriginallySubscribed = collection.collectionId && subscribedCollectionUrls.includes(collection.collectionId);

      if (isCurrentlySelected && wasOriginallySubscribed) {
        return <Badge color="green">Subscribed</Badge>;
      } else if (isCurrentlySelected && !wasOriginallySubscribed) {
        return <Badge color="blue">New</Badge>;
      } else if (!isCurrentlySelected && wasOriginallySubscribed) {
        return <Badge color="red">Removing</Badge>;
      } else {
        return <Badge color="grey">Available</Badge>;
      }
    },
    width: 100,
    minWidth: 80,
  };

  return [statusColumn, ...baseColumns];
};

interface ApplicationCollectionsTableProps {
  collections: Collection[];
  selectedCollectionUrls: string[];
  subscribedCollectionUrls: string[];
  onSelectionChange: (selectedUrls: string[]) => void;
  loading?: boolean;
}

const ApplicationCollectionsTable: React.FC<ApplicationCollectionsTableProps> = ({
  collections,
  selectedCollectionUrls,
  subscribedCollectionUrls,
  onSelectionChange,
  loading = false,
}) => {
  const [filteringText, setFilteringText] = React.useState('');
  const [currentPageIndex, setCurrentPageIndex] = React.useState(1);
  const [selectedDataSource, setSelectedDataSource] = React.useState<SelectProps.Option | null>({
    label: 'All data sources',
    value: '',
  });
  const [sortingColumn, setSortingColumn] = React.useState<{
    sortingField: string;
    isDescending: boolean;
  }>({
    sortingField: 'createdAt',
    isDescending: true,
  });
  const [showOnlySubscribed, setShowOnlySubscribed] = React.useState(true);

  // Create stable initial sort order based on original subscriptions (not current selections)
  const stableSortedCollections = React.useMemo(() => {
    let sorted = sortCollections(collections, sortingColumn.sortingField, sortingColumn.isDescending);

    // Prioritize originally subscribed collections at the top when showing all
    if (!showOnlySubscribed) {
      sorted = sorted.sort((a, b) => {
        const aIsOriginallySubscribed = a.collectionId && subscribedCollectionUrls.includes(a.collectionId);
        const bIsOriginallySubscribed = b.collectionId && subscribedCollectionUrls.includes(b.collectionId);

        if (aIsOriginallySubscribed && !bIsOriginallySubscribed) return -1;
        if (!aIsOriginallySubscribed && bIsOriginallySubscribed) return 1;
        return 0;
      });
    }

    return sorted;
  }, [collections, sortingColumn, showOnlySubscribed, subscribedCollectionUrls]); // Note: excludes selectedCollectionUrls

  // Get unique data sources
  const dataSourceOptions = React.useMemo(() => {
    const uniqueSources = Array.from(new Set(collections.map((c) => c.source))).sort();
    const options = uniqueSources.map((source) => ({ label: source, value: source }));
    return [{ label: 'All data sources', value: '' }, ...options];
  }, [collections]);

  // Filter collections using stable sort order
  let filteredItems = filterCollections(stableSortedCollections, filteringText, selectedDataSource?.value || null);

  // Apply subscribed filter if enabled
  if (showOnlySubscribed) {
    filteredItems = filteredItems.filter(
      (collection) =>
        collection.collectionId &&
        (selectedCollectionUrls.includes(collection.collectionId) || // Currently selected (including new selections)
          subscribedCollectionUrls.includes(collection.collectionId)), // Originally subscribed
    );
  }

  // Use filtered items directly (already sorted in stable order)
  const sortedItems = filteredItems;

  // Pagination
  const pageSize = 10;
  const paginatedItems = sortedItems.slice((currentPageIndex - 1) * pageSize, currentPageIndex * pageSize);
  const totalPages = Math.ceil(sortedItems.length / pageSize);

  // Convert selected URLs to selected items
  const selectedItems = React.useMemo(() => {
    return collections.filter((collection) => collection.collectionId && selectedCollectionUrls.includes(collection.collectionId));
  }, [collections, selectedCollectionUrls]);

  // Handle selection change
  const handleSelectionChange = (items: Collection[]) => {
    const urls = items.map((item) => item.collectionId).filter((id): id is string => !!id);
    onSelectionChange(urls);
  };

  return (
    <Table
      variant="embedded"
      renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
        `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
      }
      onSelectionChange={({ detail }) => handleSelectionChange(detail.selectedItems)}
      selectedItems={selectedItems}
      sortingColumn={{ sortingField: sortingColumn.sortingField }}
      sortingDescending={sortingColumn.isDescending}
      onSortingChange={({ detail }) => {
        setSortingColumn({
          sortingField: detail.sortingColumn?.sortingField || 'createdAt',
          isDescending: detail.isDescending || false,
        });
      }}
      columnDefinitions={getCollectionColumnDefinitionsWithStatus(selectedCollectionUrls, subscribedCollectionUrls)}
      columnDisplay={[
        { id: 'status', visible: true },
        { id: 'name', visible: true },
        { id: 'description', visible: true },
        { id: 'dataSource', visible: true },
        { id: 'itemCount', visible: true },
        { id: 'applicationCount', visible: true },
        { id: 'creator', visible: true },
        { id: 'createTime', visible: true },
        { id: 'lastUpdateTime', visible: true },
        { id: 'syncFrequency', visible: true },
      ]}
      enableKeyboardNavigation
      resizableColumns
      items={paginatedItems}
      loading={loading}
      loadingText="Loading collections"
      selectionType="multi"
      trackBy="collectionId"
      stickyColumns={{ first: 1, last: 0 }}
      empty={
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>No collections</b>
        </Box>
      }
      header={<Box variant="h3">Available Content Collections</Box>}
      filter={
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          <TableFilters
            filteringText={filteringText}
            selectedDataSource={selectedDataSource}
            dataSourceOptions={dataSourceOptions}
            matchCount={sortedItems.length}
            onFilteringTextChange={(text) => {
              setFilteringText(text);
              setCurrentPageIndex(1);
            }}
            onDataSourceChange={(option) => {
              setSelectedDataSource(option);
              setCurrentPageIndex(1);
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Toggle
              checked={showOnlySubscribed}
              onChange={({ detail }) => {
                setShowOnlySubscribed(detail.checked);
                setCurrentPageIndex(1);
              }}
            >
              Show only currently subscribed collections
            </Toggle>
          </div>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          pagesCount={totalPages}
          onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
        />
      }
    />
  );
};

export default ApplicationCollectionsTable;
